import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import { useTranslation } from "react-i18next";

import CopyButton from "../../../../components/buttons/CopyButton";
import { OutboundDeliveryOverviewFragment } from "../../../../models/autogenerated-deliveryService-gql/graphql";
import DeliveryStatusBadge from "../deliveryStatusBadge/DeliveryStatusBadge";
import DeliveryTimestamp from "../deliveryTimestamp/DeliveryTimestamp";
import ItemCountBadge from "../itemCountBadge/ItemCountBadge";
import "./OutboundDeliveryCard.scss";

interface OutboundDeliveryCardProps {
  delivery: OutboundDeliveryOverviewFragment;
}

type OutboundDeliveryPartner =
  OutboundDeliveryOverviewFragment["partners"][number];
type OutboundDeliveryTime = OutboundDeliveryOverviewFragment["times"][number];

const partnerLabelKeys = {
  SENDER: "sender",
  Recipient: "recipient",
} as const;

const partnerRoles = Object.keys(partnerLabelKeys) as Array<
  keyof typeof partnerLabelKeys
>;

const orderedTimeFields = [
  { type: "WSHDRWADAT", category: "PLANNED" },
  { type: "WSHDRWADAT", category: "ACTUAL" },
  { type: "END_PACKING", category: "ACTUAL" },
  { type: "DELIVERY_DATE", category: "PLANNED" },
  { type: "WSHDRKODAT", category: "ACTUAL" },
] as const;

const getTimeLabelKey = (type: string, category: string) => {
  if (type === "WSHDRWADAT" && category === "PLANNED") {
    return "goodsIssuePlannedDate";
  }

  if (type === "WSHDRWADAT" && category === "ACTUAL") {
    return "goodsIssueCompleted";
  }

  if (type === "END_PACKING" && category === "ACTUAL") {
    return "endPackingExecuted";
  }

  if (type === "DELIVERY_DATE" && category === "PLANNED") {
    return "deliveryDatePlanned";
  }

  if (type === "WSHDRKODAT" && category === "ACTUAL") {
    return "pickingCompleted";
  }

  return null;
};

const OutboundDeliveryCard = (props: OutboundDeliveryCardProps) => {
  const { t } = useTranslation();
  const { delivery } = props;

  const carrier = delivery.carrierTenantName || "-";

  const renderPartnerRows = () => {
    const partnerValues = delivery.partners.reduce<
      Partial<Record<keyof typeof partnerLabelKeys, string>>
    >((rows, partner: OutboundDeliveryPartner) => {
      if (!(partner.role in partnerLabelKeys)) {
        return rows;
      }

      const role = partner.role as keyof typeof partnerLabelKeys;

      if (!rows[role]) {
        rows[role] = partner.companyName || "-";
      }

      return rows;
    }, {});

    return partnerRoles.map((role) => (
      <div key={role} className="outbound-delivery-card__row">
        <div className="outbound-delivery-card__label">
          {t(partnerLabelKeys[role])}
        </div>
        <div className="outbound-delivery-card__value">
          {partnerValues[role] || "-"}
        </div>
      </div>
    ));
  };

  const renderTimeRows = () => {
    const timeValues = delivery.times.reduce<
      Record<string, OutboundDeliveryTime["time"]>
    >((rows, deliveryTime: OutboundDeliveryTime) => {
      const labelKey = getTimeLabelKey(
        deliveryTime.type,
        deliveryTime.category,
      );

      if (!labelKey) {
        return rows;
      }

      const timeKey = `${deliveryTime.type}:${deliveryTime.category}`;

      if (!(timeKey in rows)) {
        rows[timeKey] = deliveryTime.time;
      }

      return rows;
    }, {});

    return orderedTimeFields.map(({ type, category }) => {
      const timeKey = `${type}:${category}`;
      const labelKey = getTimeLabelKey(type, category);

      if (!labelKey) {
        return null;
      }

      return (
        <div key={timeKey} className="outbound-delivery-card__row">
          <div className="outbound-delivery-card__label outbound-delivery-card__label--time">
            {t(labelKey)}
          </div>
          <div className="outbound-delivery-card__value">
            <DeliveryTimestamp date={timeValues[timeKey]} />
          </div>
        </div>
      );
    });
  };

  return (
    <div data-testid="card-list-item" className="card-list-item">
      <div data-testid={delivery.id} className="card-list-item-card">
        <Card
          variant="outlined"
          data-testid={`outboundDeliveryCard${delivery.id}`}
          className="outbound-delivery-card"
        >
          <CardContent>
            <div className="outbound-delivery-card__header">
              <div className="outbound-delivery-card__header-row outbound-delivery-card__header-row--primary">
                <div className="outbound-delivery-card__header-id">
                  <div className="outbound-delivery-card__header-id-text">
                    <strong>{t("deliveryNumber")}:</strong>{" "}
                    {delivery.deliveryNumber || "-"}
                  </div>
                  {delivery.deliveryNumber && (
                    <div className="outbound-delivery-card__header-id-copy">
                      <CopyButton
                        tooltip="Copy ID"
                        value={delivery.deliveryNumber}
                        size="small"
                        variant="text"
                      />
                    </div>
                  )}
                </div>
              </div>
              <div className="outbound-delivery-card__header-row outbound-delivery-card__header-row--meta">
                <div className="outbound-delivery-card__header-center">
                  <ItemCountBadge count={delivery.itemCount} />
                </div>
              </div>
              <div className="outbound-delivery-card__header-row outbound-delivery-card__header-row--status">
                {delivery.status && (
                  <div className="outbound-delivery-card__header-right">
                    <DeliveryStatusBadge status={delivery.status} />
                  </div>
                )}
              </div>
            </div>

            <div className="page-content-divider" />

            <div className="outbound-delivery-card__section">
              <div className="outbound-delivery-card__row">
                <div className="outbound-delivery-card__label--bold">
                  {t("packingStatus")}
                </div>
                <div className="outbound-delivery-card__value">
                  <DeliveryStatusBadge status={delivery.packingStatus} />
                </div>
              </div>
              <div className="outbound-delivery-card__row">
                <div className="outbound-delivery-card__label--bold">
                  {t("pickingStatus")}
                </div>
                <div className="outbound-delivery-card__value">
                  <DeliveryStatusBadge status={delivery.pickingStatus} />
                </div>
              </div>
              <div className="outbound-delivery-card__row">
                <div className="outbound-delivery-card__label--bold">
                  {t("goodsIssueStatus")}
                </div>
                <div className="outbound-delivery-card__value">
                  <DeliveryStatusBadge status={delivery.goodsIssueStatus} />
                </div>
              </div>
            </div>

            <div className="page-content-divider" />

            <div className="outbound-delivery-card__section">
              {renderPartnerRows()}
              <div className="outbound-delivery-card__row">
                <div className="outbound-delivery-card__label">
                  {t("carrier")}
                </div>
                <div className="outbound-delivery-card__value">{carrier}</div>
              </div>
            </div>

            <div className="page-content-divider" />

            <div className="outbound-delivery-card__section">
              {renderTimeRows()}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default OutboundDeliveryCard;
